import React from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const AboutUs = () => {
  const sections = [
    {
      text: `Anagha Engineers is a Hyderabad-based engineering and EPC company operating across the electrical power sector. We combine strong technical capability, industry partnerships, and trusted leadership to deliver solutions that power India’s growing infrastructure.`,
      image: "aboutus/About.png",
      imgLeft: true,
    },
    {
      title: "Our Legacy",
      text: `Founded under the guidance and mentorship of Mr. E. Kishan Rao, retired senior official from the Department of Space (ISRO), Anagha Engineers is built on the principles of precision, integrity, and engineering excellence.`,
      image: "3222p.png",
      imgLeft: false,
    },
    {
      title: "Leadership & Governance",
      text: `Mr. E. Avinash, CEO, drives the company’s strategy, growth, and long-term vision. With deep exposure to the electrical power sector and modern industry practices, he ensures the organization remains agile, innovative, and customer-focused.

Mr. Devender Reddy, Partner, heads commercial operations including procurement, contracts, and financial execution.`,
      image: "31p.png",
      imgLeft: true,
    },

    {
      title: "Vision & Mission",
      text: `Our mission is to deliver value to our customers by providing engineering services, supplies, construction, the operation, and management of infrastructure, industrial, and energy projects.

We wish to become a leader in Turnkey / EPC projects for the development of transmission networks & civil infrastructure for the next generation. To build and operate a transmission network that is safe, reliable, and enables low-carbon energy transfer to promote renewable energy through innovative and best practices.`,
      image: "aboutus/one.png",
      imgLeft: true,
    },
    {
      title: "Quality Policy",
      text: `Anagha Engineers is committed to providing quality work to our customers that meets the project standards and specifications for materials, workmanship, tolerances, schedules and public service while maintaining profitability and competitiveness.

Anagha Engineers ensures continual improvement through quality processes directed by a strong management team.

To promote our ongoing process of customer satisfaction, we motivate our employees at all levels to improve the quality of all contractual obligations with a safe working environment.`,
      image: "aboutus/two.png",
      imgLeft: false,
    },
    {
      title: "Safety Policy",
      text: `Our Occupational / Workplace Health and Safety Policy helps us preserve the best possible work conditions for our employees. Every employee has a right to feel safe at work. Our company is committed to follow legal standards and create a hazard-free workplace.

Anagha Engineers promotes a “Zero Injury Philosophy” as our safety culture. We strive for the highest standards across all operations through risk assessment, proactive safety management, and continuous improvement.

Every individual is responsible for their own safety and the safety of those around them, upholding and contributing to our strong safety culture.`,
      image: "aboutus/three.png",
      imgLeft: true,
    },
  ];

  return (
    <Box sx={{ background: "#f5f5f5" }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        sx={{
          position: "relative",
          height: { xs: "35vh", md: "45vh" },
          width: "100%",
          backgroundImage: "url('/aboutus/aboutus.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.35)",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <Typography
            variant="h2"
            sx={{
              position: "relative",
              fontWeight: 800,
              letterSpacing: 1,
              textShadow: "2px 2px 15px rgba(0,0,0,0.6)",
              display: "inline-block",
              animation: "letterSpace 1.8s ease-out forwards",
              "@keyframes letterSpace": {
                "0%": { letterSpacing: "6px", opacity: 0.2 },
                "100%": { letterSpacing: "1px", opacity: 1 },
              },
            }}
          >
            About Us
          </Typography>
        </motion.div>

        {/* 🔥 Underline Added */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 90, opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          style={{
            height: "4px",
            backgroundColor: "#ffffff",
            borderRadius: "2px",
            marginTop: "15px",
          }}
        />
      </Box>

      {/* Dynamic Sections */}
      {sections.map((section, idx) => (
        <Box
          key={idx}
          sx={{
            py: { xs: 8, md: 12 },
            background: idx % 2 === 0 ? "#ffffff" : "#f0f4f8",
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={6}
              alignItems="center"
              justifyContent="center"
              direction={section.imgLeft ? "row" : "row-reverse"}
            >
              {/* Image */}
              <Grid item xs={12} md={5}>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                >
                  <Box
                    component="img"
                    src={section.image}
                    alt={section.title}
                    sx={{
                      width: "100%",
                      maxWidth:
                        section.image === "3222p.png"
                          ? 200
                          : section.image === "31p.png"
                            ? 240
                            : 400,   
                      borderRadius: 6,
                      boxShadow: "0 15px 40px rgba(0,0,0,0.15)",
                      mx: "auto",
                      transition: "transform 0.6s ease, box-shadow 0.6s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
                      },
                    }}
                  />
                </motion.div>
              </Grid>

              {/* Text */}
              <Grid item xs={12} md={7}>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                >
                  <Box sx={{ maxWidth: 650, mx: "auto" }}>
                    {section.title && (
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          color: "#1e3a8a",
                          mb: 3,
                          position: "relative",
                          display: "inline-block",
                          letterSpacing: 0.5,
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: -8,
                            left: 0,
                            width: "70px",
                            height: "4px",
                            backgroundColor: "#1e3a8a",
                            borderRadius: 2,
                          },
                        }}
                      >
                        {section.title}
                      </Typography>
                    )}

                    {section.text.split("\n\n").map((para, i) => (
                      <Typography
                        key={i}
                        sx={{
                          lineHeight: 1.9,
                          fontSize: "1.08rem",
                          color: "#3b3b3b",
                          textAlign: "justify",
                          mb: 2.5,
                        }}
                      >
                        {para}
                      </Typography>
                    ))}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}
    </Box>
  );
};

export default AboutUs;
